import { Location } from "@angular/common";
import { AfterViewInit, Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { CalendarView, CalendarEvent, DAYS_OF_WEEK } from "angular-calendar";
import { startOfWeek, format, endOfWeek, startOfDay, endOfDay } from "date-fns";
import * as moment from "moment";
import { NgxSpinnerService } from "ngx-spinner";
import { Studio } from "src/app/models/studio-model";
import { User } from "src/app/models/user-model";
import { ReservationService } from "src/app/services/reservation/reservation.service";
import { StudioService } from "src/app/services/studio/studio.service";
import { GenerateErrors } from "src/app/utils/generate-errors";
import { Webflow } from "src/app/utils/webflow";
import { environment } from "src/environments/environment";
import Swal from "sweetalert2";
import {
  NgbCalendar,
  NgbDate,
  NgbDateStruct,
} from "@ng-bootstrap/ng-bootstrap";
import { DiscountPeriod } from "src/app/models/discount-period-model";
import { CustomAdapter } from "src/app/services/date-formatter/date-parser.service";
import { Simulator } from "src/app/models/simulator-model";

@Component({
  selector: "app-planning-booking",
  templateUrl: "./planning-booking.component.html",
  styleUrls: ["./planning-booking.component.scss"],
})
export class PlanningBookingComponent implements OnInit, AfterViewInit {
  view: CalendarView = CalendarView.Week;
  studios: any;
  viewDate: Date = new Date();
  events: CalendarEvent[] = []; // Initially an empty array
  currentRange: { start: string; end: string } | null = null;
  params: any;
  currentStudio: Studio | null = null;
  currentUser: User | null = null;
  currentStudioId: any | null = null;
  selectedStartDate: string = "--- ---"; // To hold the formatted start date
  selectedEndDate: string = "--- ---"; // To hold the formatted end date
  startDate: any | null = "--- ---";
  endDate: any | null = "--- ---";
  allHours: string[] = Array.from({ length: 24 }, (_, i) =>
    moment({ hour: i }).format("HH:00")
  );
  hours: string[] = [...this.allHours]; // Available hours for the selected date

  messageListener: any;
  weekStartsOn: number = DAYS_OF_WEEK.MONDAY;

  form!: FormGroup;
  loading: boolean = true; // Set loading to true initially
  submitted = false;

  toDay: NgbDate | null;

  availableDiscounts: DiscountPeriod[] = [];
  availableDiscountRate: number | null = null;

  discountDays: Set<number> = new Set();

  // map the days with there values for possibility to work with theme in the ngbdatepicker
  dayMap: { [key: string]: number } = {
    sunday: 7,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  dropdownOpen = false;
  selectedHour: any = null;

  ph: number = 0;
  nh: number = 0;
  final_price: number = 0;
  final_price_after_apply_promo: number = 0;
  reduce_value: string = "0";
  price_fees: any;

  promo_code_is_valid: boolean = false;
  promo_code_error: string = "";
  promo_code_value: string = "";
  checking_promo_code: boolean = false;

  discount_periods_found: boolean = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private studioService: StudioService,
    private location: Location,
    private reservationService: ReservationService,
    private formBuilder: FormBuilder,
    private calendar: NgbCalendar,
    private customAdapter: CustomAdapter
  ) {
    this.form = this.formBuilder.group({
      startDate: ["", Validators.required],
      startDateMockup: ["", Validators.required],
      startTime: ["00:00", Validators.required],
      nbr_hours: [0],
      promo_code: [""],
    });
    this.toDay = this.calendar.getToday();
  }

  //validator submit
  get formControl() {
    return this.form.controls;
  }

  ngOnInit(): void {
    // Add the message listener when the component initializes
    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== environment.webflow_url) {
        return;
      }

      const { scrollToElementId } = event.data;

      if (scrollToElementId) {
        const element = document.getElementById(scrollToElementId);
        if (element) {
          Webflow.setIFrameHeight(false, 100);
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    };

    // Attach the event listener to the window
    window.addEventListener("message", this.messageListener);
    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = { ...params }; // Create a shallow copy of params

      this.promo_code_value = params.promo_code;
      this.form.get("promo_code")?.setValue(this.promo_code_value);

      // prevent user to change data in the url
      let validStoredData =
        JSON.stringify(this.params) === localStorage.getItem("booking");
      if (
        params.start_date &&
        params.end_date &&
        params.studio &&
        validStoredData
      ) {
        this.studioService
          .get_one(params.studio)
          .subscribe({
            next: (studio) => {
              this.currentStudio = Studio.attributeDataToJson(studio);
              this.currentStudioId = this.currentStudio.id;
              // initialyze discount periods from params (start date)
              const dateOnly: string = this.params.start_date.split(" ")[0];
              const timeOnly: string = this.params.start_date.split(" ")[1];

              this.form.get("startTime")?.setValue(timeOnly);
              this.updateAvailableTimes(dateOnly);
              this.get_discount_periods(this.customAdapter.fromModel(dateOnly));
              // Set default value (today)
              this.form
                .get("startDateMockup")
                ?.setValue(this.customAdapter.fromModel(dateOnly));
              this.discountDays = new Set(
                this.currentStudio?.discount_periods?.map(
                  (dp) => this.dayMap[dp.day_selected.toLowerCase()]
                )
              );
              // Watch for date selection changes
              this.form
                .get("startDateMockup")
                ?.valueChanges.subscribe((selectedDate) => {
                  const formatted = this.ngbDateToString(selectedDate);
                  this.form.get("startDate")?.setValue(formatted); // Set default value
                  this.updateAvailableTimes(formatted);
                  this.get_discount_periods(selectedDate);
                });
              this.selectedStartDate = moment(
                params.start_date,
                "YYYY-MM-DD HH:mm"
              ).format("DD-MM-YYYY HH:mm");
              this.selectedEndDate = moment(
                params.end_date,
                "YYYY-MM-DD HH:mm"
              ).format("DD-MM-YYYY HH:mm");
              this.setupValueChanges();
            },
            error: (error) => {
              if (GenerateErrors.check_if_is_custom_error(error)) {
                Swal.fire({
                  title: "Attention",
                  text: GenerateErrors.get_message_error(error),
                  icon: "error",
                  position: "top",
                });
              } else {
                Swal.fire({
                  title: "Attention",
                  text: `Le studio n'existe pas!`,
                  icon: "error",
                  position: "top",
                });
              }
              this.currentStudio = null;
            },
          })
          .add(() => {
            this.reservationService
              .busyIntervals(this.params)
              .subscribe((res) => {
                // check if the innterval is free
                if (res) {
                  Webflow.setIFrameHeight();
                  Swal.fire({
                    icon: "info",
                    title: "Attention",
                    html: `Cette plage horaire<br>
                  <strong>Date début :</strong> ${this.selectedStartDate}<br>
                  <strong>Date fin :</strong> ${this.selectedEndDate}<br>
                  est déjà prise. Veuillez en sélectionner une autre.`,
                    position: "top",
                    confirmButtonText: "Choisir une autre date",
                  }).then((result) => {
                    if (result.isConfirmed) {
                      this.resetForm();
                    }
                  });
                }
              });
            this.loading = false;
            setTimeout(() => {
              this.fillCalendarWithSelectedRange(this.getFormattedWeekDates());
              Webflow.setIFrameHeight(true, 100);
            }, 500);
          });
      } else {
        this.location.back();
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener("message", this.messageListener);
  }

  ngAfterViewInit(): void {
    Webflow.setIFrameHeight();
  }

  switchToStudio(studioId: string): void {
    this.loading = true; // Set loading to true when switching studio
    const id = parseInt(studioId, 10);
    this.currentStudio =
      this.studios.find((studio) => studio.id === id) || null;
    if (this.currentStudio) {
      this.currentStudioId = this.currentStudio.id;
    }
    // send the formatted start and end dates
    if (
      this.currentStudioId &&
      this.currentStudioId != null &&
      this.currentStudioId != "null"
    ) {
      this.fillCalendarWithSelectedRange(this.getFormattedWeekDates());
    }
    // Simulate an image loading delay (optional, for demonstration)
    setTimeout(() => {
      this.loading = false; // Reset loading after a delay (remove this in real use case)
    }, 1500);
  }

  getFormattedWeekDates() {
    const startOfWeekDate = startOfWeek(this.viewDate, {
      weekStartsOn: this.weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    });
    const endOfWeekDate = endOfWeek(this.viewDate, {
      weekStartsOn: this.weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    });

    const formattedStart = format(startOfWeekDate, "yyyy-MM-dd");
    const formattedEnd = format(endOfWeekDate, "yyyy-MM-dd");

    return { start: formattedStart, end: formattedEnd };
  }

  // Handle the selected time range from CalendarHeaderComponent
  fillCalendarWithSelectedRange(range: { start: string; end: string }): void {
    this.currentRange = range; // Store the range for later use
    if (range && this.currentStudioId) {
      this.reservationService
        .fetchEvent(
          this.currentRange.start,
          this.currentRange.end,
          this.currentStudioId
        )
        .subscribe(
          (events: CalendarEvent[]) => {
            const discountEvents = this.getDiscountEventsForSelectedRange(
              range,
              this.currentStudio.discount_periods
            );
            // Assign the fetched events to the local events array
            this.events = [...events, ...discountEvents];
          },
          (error) => {
            console.error("Error fetching events:", error);
          }
        )
        .add(() => {});
    }
  }

  getSelectedDate(date: Date) {
    const startDateTime = moment(date, "DD/MM/YYYY HH:mm").minutes(0);

    this.form.patchValue({
      startDate: startDateTime.format("YYYY-MM-DD"),
      startTime: startDateTime.format("HH:mm"),
      startDateMockup: this.customAdapter.fromModel(
        startDateTime.format("YYYY-MM-DD")
      ),
    });
    Webflow.scrollToElementById("fillDates");
  }

  // go to calendar view "start date range"
  goToCalendarView() {
    const momentDate = moment(this.selectedStartDate, "DD-MM-YYYY HH:mm", true);
    this.viewDate = momentDate.toDate(); // Set viewDate if the date is valid
  }

  // Set up value changes to update selectedStartDate and selectedEndDate
  setupValueChanges() {
    let previousValues = this.form.value;

    const startDateTime = moment(this.selectedStartDate, "DD/MM/YYYY HH:mm");
    const endDateTime = moment(this.selectedEndDate, "DD/MM/YYYY HH:mm");

    this.form.patchValue({
      startDate: startDateTime.format("YYYY-MM-DD"),
      startTime: startDateTime.format("HH:mm"),
      nbr_hours: endDateTime.diff(startDateTime, "hours"),
    });
    this.form.valueChanges.subscribe((formValues) => {
      const { startDate, startTime, nbr_hours, promo_code } = formValues;

      const startDateMoment = moment(
        `${startDate} ${startTime}`,
        "YYYY-MM-DD HH:mm"
      );
      const endDateMoment = startDateMoment.clone().add(nbr_hours, "hours");

      if (startDateMoment.isValid()) {
        this.selectedStartDate = startDateMoment.format("DD-MM-YYYY HH:mm");
        this.selectedEndDate = endDateMoment.format("DD-MM-YYYY HH:mm");
        this.goToCalendarView();
        this.calculate_price();
      }

      // Check if any relevant fields changed
      if (
        startDate !== previousValues.startDate ||
        startTime !== previousValues.startTime ||
        nbr_hours !== previousValues.nbr_hours
      ) {
      }

      previousValues = formValues;
    });
  }

  resetForm() {
    this.selectedStartDate = this.selectedEndDate = "--- ---";
    this.form.patchValue({
      startDate: "",
      startTime: "00:00",
      nbr_hours: this.currentStudio.nbr_min_hours,
    });
  }

  onSubmit() {
    this.submitted = true;
    // Dynamically update the min validator based on currentStudio's min hours
    const nbrHoursControl = this.form.get("nbr_hours");
    if (this.currentStudio?.nbr_min_hours && nbrHoursControl) {
      nbrHoursControl.setValidators([
        Validators.required,
        Validators.min(this.currentStudio.nbr_min_hours),
        Validators.max(23),
      ]);
      nbrHoursControl.updateValueAndValidity(); // Update the form validation state
    }
    if (this.form.invalid) {
      Webflow.setIFrameHeight();
      if (nbrHoursControl?.hasError("max")) {
        Swal.fire({
          icon: "info",
          title: "Attention",
          text: "Le Nombre d'heures maximal est 23H.",
          position: "top",
        });
      } else if (nbrHoursControl?.hasError("min")) {
        Swal.fire({
          icon: "info",
          title: "Attention",
          text: `Le nombre d'heures minimal pour le studio ${this.currentStudio.name} est ${this.currentStudio?.nbr_min_hours}H.`,
          position: "top",
        });
      } else {
        Swal.fire({
          icon: "info",
          title: "Attention",
          text: "Veuillez vérifier vos informations.",
          position: "top",
        });
      }
      return;
    }

    // Convert to moment objects
    const startDateTime = moment(this.selectedStartDate, "DD-MM-YYYY HH:mm");
    const endDateTime = moment(this.selectedEndDate, "DD-MM-YYYY HH:mm");
    // Create the payload in the required format
    const data = {
      start_date: startDateTime.format("YYYY-MM-DD HH:mm"),
      end_date: endDateTime.format("YYYY-MM-DD HH:mm"),
      studio: this.currentStudio.id.toString(), // Replace with the actual studio ID if needed
    };

    if (this.promo_code_is_valid) {
      data["promo_code"] = this.promo_code_value;
    }

    // 1 - save data in localStorage
    localStorage.setItem("booking", JSON.stringify(data));
    // 2 - search if has any booking in this dates
    this.reservationService.busyIntervals(data).subscribe((res) => {
      // check if the innterval is free
      if (res) {
        // 3 - if range is busy
        Webflow.setIFrameHeight();
        Swal.fire({
          icon: "info",
          title: "Attention",
          html: `Cette plage horaire<br>
          <strong>Date début :</strong> ${this.selectedStartDate}<br>
          <strong>Date fin :</strong> ${this.selectedEndDate}<br>
          est déjà prise. Veuillez en sélectionner une autre.`,
          position: "top",
          confirmButtonText: "choisir une autre date",
        }).then((result) => {
          if (result.isConfirmed) {
            this.resetForm();
          }
        });
      } else {
        // 4 - if not has any date redirect to payment page ( get datas of booking from localStorage => show studio data : make it like this https://www.studiomap.fr/app?action=reservation-confirm)
        Webflow.myAction(this.router, "payment-form", data);
      }
    });
  }

  updateAvailableTimes(selectedDate: string) {
    if (!selectedDate) {
      this.hours = [...this.allHours];
      return;
    }

    const selectedMoment = moment(selectedDate, "YYYY-MM-DD");

    const today = moment().startOf("day");
    const currentMoment = moment();
    const currentHour = currentMoment.hour();
    const currentMinutes = currentMoment.minutes();

    // Determine default hour: If minutes > 0, select next hour
    const reservationHour = this.form.value.startTime;
    // const reservationHour = this.params.start_date.split(' ')[1];

    if (selectedMoment.isSame(today, "day")) {
      // If today, exclude current hour if minutes > 0
      this.hours = this.allHours.filter((time) => {
        const hourValue = parseInt(time.split(":")[0], 10);
        return currentMinutes > 0
          ? hourValue > currentHour
          : hourValue >= currentHour;
      });
    } else {
      // If another day, show all hours from 00:00
      this.hours = [...this.allHours];
    }

    // Auto-select the default hour if it's available, otherwise select the first available hour
    setTimeout(() => {
      this.form
        .get("startTime")
        ?.setValue(
          this.hours.includes(reservationHour) ? reservationHour : this.hours[0]
        );
    }, 0);
  }

  get_discount_periods(selectedDate: any) {
    // 🔍 Get weekday number from NgbCalendar (1 = Monday, ..., 7 = Sunday)
    const weekday = this.calendar.getWeekday(selectedDate);

    // 🔄 Map that weekday number to the string like "monday", "tuesday", ...
    const discountDay = Object.entries(this.dayMap).find(
      ([_, value]) => value === weekday
    )?.[0]; // grab the key (like 'monday')

    // 🎯 Match against discount_periods
    if (discountDay) {
      this.availableDiscounts =
        this.currentStudio.discount_periods.filter(
          (dp) => dp.day_selected.toLowerCase() === discountDay
        ) || null;
    } else {
      this.availableDiscounts = [];
    }
  }

  ngbDateToString(date: NgbDateStruct): string {
    return moment({
      year: date.year,
      month: date.month - 1, // Moment uses 0-based months
      day: date.day,
    }).format("YYYY-MM-DD");
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  selectHour(hour: any) {
    this.selectedHour = hour;
    this.form.get("startTime").setValue(hour);
    this.dropdownOpen = false;
  }

  isDiscountDay = (date: NgbDate): boolean => {
    const day = this.calendar.getWeekday(date); // ISO day: 1=Mon, ..., 7=Sun
    return this.discountDays.has(day);
  };

  isHourInDiscount(hour: string): boolean {
    if (!this.availableDiscounts || this.availableDiscounts.length === 0)
      return false;

    const hourMoment = moment(hour, "HH:mm");

    return this.availableDiscounts.some((discount) => {
      const start = moment(discount.start_time, "HH:mm:ss.SSS");
      const end = moment(discount.end_time, "HH:mm:ss.SSS");
      return hourMoment.isSameOrAfter(start) && hourMoment.isBefore(end);
    });
  }

  getDiscountRateForHour(hour: string): number | null {
    const hourMoment = moment(hour, "HH:mm");

    const matching = this.availableDiscounts.find((discount) => {
      const start = moment(discount.start_time, "HH:mm:ss.SSS");
      const end = moment(discount.end_time, "HH:mm:ss.SSS");
      return hourMoment.isSameOrAfter(start) && hourMoment.isBefore(end);
    });

    return matching ? matching.discount_rate : null;
  }

  check_promo_code() {
    const startDateTime = moment(this.selectedStartDate, "DD-MM-YYYY HH:mm");
    const endDateTime = moment(this.selectedEndDate, "DD-MM-YYYY HH:mm");
    // Create the payload in the required format

    this.promo_code_error = "";
    if (this.form.get("promo_code")?.value == "") {
      this.promo_code_error = "Veuillez saisir une code promotionnel";
      return;
    }

    this.checking_promo_code = true;

    var obj = {
      ph: this.currentStudio?.price,
      nh: Number(this.form.get("nbr_hours")?.value),
      promo_code: this.form.get("promo_code")?.value,
      studio: this.currentStudio.id.toString(),
      start_date: startDateTime.format("YYYY-MM-DD HH:mm"),
      end_date: endDateTime.format("YYYY-MM-DD HH:mm"),
    };
    this.studioService.get_final_price(obj).subscribe((res) => {
      if (res.promo_code_validity) {
        this.final_price = res.final_price;
        this.final_price_after_apply_promo = res.final_price_after_apply_promo;
        this.reduce_value = (
          this.final_price - this.final_price_after_apply_promo
        )
          .toFixed(2)
          .toString();
        this.discount_periods_found = res.discount_periods_found;
        this.promo_code_is_valid = true;
        this.promo_code_value = this.form.get("promo_code")?.value;
      } else {
        this.promo_code_error = "Le code promotionnel entré n'est pas valide";
        this.promo_code_is_valid = false;
        this.promo_code_value = "";
      }
      this.checking_promo_code = false;
      Webflow.setIFrameHeight();
    });
  }

  dettach_promo_code() {
    this.promo_code_error = "";
    this.promo_code_value = "";
    this.promo_code_is_valid = false;
    this.form.get("promo_code")?.setValue("");
    this.calculate_price();
    Webflow.setIFrameHeight();
  }

  calculate_price() {
    const startDateTime = moment(this.selectedStartDate, "DD-MM-YYYY HH:mm");
    const endDateTime = moment(this.selectedEndDate, "DD-MM-YYYY HH:mm");
    var obj = {
      ph: this.currentStudio.price,
      nh: Number(this.form.value.nbr_hours),
      studio: this.currentStudio?.id.toString(),
      start_date: startDateTime.format("YYYY-MM-DD HH:mm"),
      end_date: endDateTime.format("YYYY-MM-DD HH:mm"),
    };

    if (this.promo_code_value != "") {
      obj["promo_code"] = this.promo_code_value;
    }

    this.studioService.get_final_price(obj).subscribe((res) => {
      this.final_price = res.final_price;
      this.final_price_after_apply_promo = res.final_price_after_apply_promo;
      this.promo_code_is_valid = res.promo_code_validity;
      this.discount_periods_found = res.discount_periods_found;
      this.reduce_value = (
        this.final_price - this.final_price_after_apply_promo
      )
        .toFixed(2)
        .toString();
      Webflow.setIFrameHeight();
    });
    this.studioService.get_price_fees().subscribe((res) => {
      this.price_fees = Simulator.attributeToJson(res);
    });
  }

  getDiscountEventsForSelectedRange(
    range: { start: string; end: string },
    discounts: any[]
  ): CalendarEvent[] {
    const events: CalendarEvent[] = [];
    const startDate = new Date(range.start);
    const endDate = new Date(range.end);

    const WEEKDAY_MAP: { [key: string]: number } = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };

    discounts
      .filter((d) => d.is_active)
      .forEach((discount) => {
        const targetDayIndex = WEEKDAY_MAP[discount.day_selected.toLowerCase()];
        const current = new Date(startDate);

        while (current <= endDate) {
          if (current.getDay() === targetDayIndex) {
            // Build the start date/time
            const start = new Date(current);
            const end = new Date(current);

            const [startHour, startMinute, startSecondAndMs] =
              discount.start_time.split(":");
            const [startSecond, startMs = "0"] = startSecondAndMs.split(".");
            start.setHours(+startHour, +startMinute, +startSecond, +startMs);

            const [endHour, endMinute, endSecondAndMs] =
              discount.end_time.split(":");
            const [endSecond, endMs = "0"] = endSecondAndMs.split(".");
            end.setHours(+endHour, +endMinute, +endSecond, +endMs);

            events.push({
              title: `- ${discount.discount_rate}%`,
              start,
              end,
              color: {
                primary: "#ff0000",
                secondary: "#FAE3E3",
              },
            });
          }
          current.setDate(current.getDate() + 1);
        }
      });

    return events;
  }
}
